import { useMemo } from "react";
import { McqOptions } from "./McqOptions";
import { QuestionActions } from "./QuestionActions";
import { QuestionFormField } from "./QuestionFormField";
import { QuestionImageUpload } from "./QuestionImageUpload";
import { Switch } from "@headlessui/react";

// Utility function for clamping values
const clamp = (val, min, max) => Math.min(max, Math.max(min, val));

export const PassageQuestionItem = ({
  question,
  stage,
  data,
  setData,
  imagePreviews,
  setImagePreviews,
  openPublishModalIndex,
  setOpenPublishModalIndex,
  handlePublishDiscussion,
  DeleteQuestion,
  handleQuestionChange,
}) => {
  const fullIndex = useMemo(
    () => data.additional_questions.findIndex((q) => q?.id === question?.id),
    [data.additional_questions, question?.id]
  );

  const showOptionsField = question.answer_type === "mcq";
  const showCorrectAnswerField = question.answer_type !== "url";
  const isURLType =
    question.answer_type === "url" || question.answer_type === "varchar";

  const commonFields = [
    {
      id: `question-${fullIndex}`,
      name: "question",
      type: "text",
      value: question.question || "",
      onChange: (e) => handleQuestionChange(fullIndex, e),
      className:
        "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
      placeholder: " ",
      maxLength: 255,
      required: true,
      label: `Question ${fullIndex + 1}*`,
      colSpan: "col-span-12 md:col-span-5",
    },
    {
      id: `type-${fullIndex}`,
      name: "answer_type",
      type: "select",
      value: question.answer_type || "",
      onChange: (e) => handleQuestionChange(fullIndex, e),
      className:
        "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
      required: true,
      label: "Type*",
      options: [
        { value: "", label: "Select Type" },
        ...(question.stage !== 1
          ? [{ value: "url", label: "URL" }]
          : [
              { value: "boolean", label: "True/False" },
              { value: "varchar", label: "Short Answer" },
              { value: "mcq", label: "Multiple Choice" },
            ]),
      ],
      colSpan: "col-span-12 md:col-span-2",
    },
    {
      id: `time-${fullIndex}`,
      name: "time_limit_seconds",
      type: "number",
      value: question.time_limit_seconds || "",
      onChange: (e) =>
        handleQuestionChange(fullIndex, {
          target: { name: "time_limit_seconds", value: e.target.value },
        }),
      onBlur: (e) => {
        const clamped = clamp(parseInt(e.target.value) || 5, 5, 600);
        handleQuestionChange(fullIndex, {
          target: { name: "time_limit_seconds", value: clamped },
        });
      },
      className:
        "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
      placeholder: " ",
      min: 5,
      max: 600,
      required: true,
      label: "Time Limit (5–600 sec)*",
      colSpan: "col-span-12 md:col-span-2",
    },
  ];

  return (
    <div className="md:grid grid-cols-12 w-full md:space-y-0 space-y-6 gap-6 mb-6 p-4 bg-gray-50 rounded-lg">
      {commonFields.map((field, fieldIndex) => (
        <div key={fieldIndex} className={`${field.colSpan}`}>
          <QuestionFormField {...field} />
        </div>
      ))}

      {/* Image Upload */}
      {stage === 1 && (
        <div className="col-span-12 md:col-span-2 flex items-center justify-center">
          <QuestionImageUpload
            setData={setData}
            questionIndex={fullIndex}
            hasImage={question.has_image}
            setImagePreviews={setImagePreviews}
            imagePreviews={imagePreviews}
            question={question}
          />

          {(question.answer_type === "boolean" ||
            question.answer_type === "mcq") && (
            <div className="flex items-center gap-2 ml-4">
              <label
                htmlFor={`is_justified-${fullIndex}`}
                className="text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                Justify
              </label>
              <Switch
                id={`is_justified-${fullIndex}`}
                name="is_justified"
                checked={question.is_justified || false}
                onChange={(enabled) =>
                  handleQuestionChange(fullIndex, {
                    target: {
                      name: "is_justified",
                      value: enabled,
                    },
                  })
                }
                className={`${
                  question.is_justified ? "bg-orange-600" : "bg-gray-200"
                } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500`}
              >
                <span
                  className={`${
                    question.is_justified ? "translate-x-6" : "translate-x-1"
                  } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                />
              </Switch>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="col-span-12 md:col-span-1 flex items-center justify-center space-x-2">
        <QuestionActions
          questionIndex={fullIndex}
          openPublishModalIndex={openPublishModalIndex}
          setOpenPublishModalIndex={setOpenPublishModalIndex}
          handlePublishDiscussion={handlePublishDiscussion}
          onDeleteQuestion={DeleteQuestion}
          question={question}
        />
      </div>

      {/* MCQ Options */}
      {showOptionsField && (
        <McqOptions
          questionIndex={fullIndex}
          question={question}
          onOptionsChange={(updatedFields) => {
            setData((prev) => ({
              ...prev,
              additional_questions: prev.additional_questions.map((q, i) => {
                if (i !== fullIndex) return q;
                return {
                  ...q,
                  ...updatedFields,
                };
              }),
            }));
          }}
        />
      )}

      {/* Correct Answer Field */}
      {showCorrectAnswerField && (
        <div className="col-span-12 mt-4">
          <QuestionFormField
            id={`correct-${fullIndex}`}
            name="correct_answer"
            type="text"
            value={question.correct_answer || ""}
            onChange={(e) => handleQuestionChange(fullIndex, e)}
            className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer"
            placeholder=" "
            required={!isURLType}
            label={`Correct Answer${!isURLType ? "*" : ""}`}
          />
        </div>
      )}
    </div>
  );
};
